import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthCard, AuthError, authButtonCls, authInputCls, authLabelCls } from "@/components/AuthCard";
import { ContinueWith } from "@/components/ContinueWith";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { getSignupStatus, signInErrorMessage, startSignup, type SignupStatus } from "@/lib/auth";
import { BOOK_A_CALL_URL } from "@/lib/legal";

const MIN_PASSWORD = 12;

/** /signup: Continue with GitHub, or email and password (a 6-digit code is
 * emailed next). GitHub sends a refused signup back here with ``?error=``. */
export function Signup() {
  const [params] = useSearchParams();
  const invite = params.get("invite") ?? undefined;
  // Arrived from the link Phantm sends with a Priority approval (ADR-0004).
  // Only wording: the approval itself is checked once the email is proven.
  const priority = params.has("priority");
  const navigate = useNavigate();
  const [status, setStatus] = useState<SignupStatus | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(() => signInErrorMessage(params.get("error"), params.get("via")));
  const [submitting, setSubmitting] = useState(false);
  // From the Turnstile widget: the check that the person is human.
  const [turnstileToken, setTurnstileToken] = useState("");

  useEffect(() => {
    getSignupStatus(invite).then(setStatus);
  }, [invite]);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const canSubmit =
    email.trim() !== "" && password.length >= MIN_PASSWORD && turnstileToken !== "" && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      await startSignup(email.trim(), password, invite, turnstileToken);
      navigate("/signup/verify", { state: { email: email.trim() } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (status === null) {
    return <AuthCard title="Create your Phantm account">{null}</AuthCard>;
  }

  if (!status.signup_open) {
    const badLink = status.mode === "invite_only" && status.invite_valid === false;
    return (
      <AuthCard title="Create your Phantm account">
        <p className="text-center text-sm text-fg-muted">
          {error ||
            (badLink
              ? "This signup link is invalid, used or expired. Ask Phantm for a new one."
              : "Signups are closed right now.")}
        </p>
        <p className="text-center text-xs">
          <Link to="/login" className="text-fg-muted hover:text-electric-blue">
            Already have an account? Sign in
          </Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Create your Phantm account"
      subtitle={priority ? "Use the email Phantm approved to get your Benchmark" : "First week free · no card to start"}
      onSubmit={handleSubmit}
    >
      <AuthError message={error} />
      <ContinueWith providers={status.sign_in_providers} origin="signup" invite={invite} />
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="email" className={authLabelCls}>Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            autoFocus
            className={authInputCls}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="password" className={authLabelCls}>Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className={authInputCls}
          />
          <p className={tooShort ? "text-xs text-block-red" : "text-xs text-fg-faint"}>
            At least {MIN_PASSWORD} characters.
          </p>
        </div>
      </div>
      {status.turnstile_site_key && (
        <TurnstileWidget siteKey={status.turnstile_site_key} onToken={setTurnstileToken} />
      )}
      <button type="submit" disabled={!canSubmit} className={authButtonCls}>
        {submitting ? "Sending code…" : "Continue"}
      </button>
      {!priority && (
        <p className="rounded-md border border-line-subtle px-3 py-2 text-center text-xs text-fg-muted">
          Running AI at scale?{" "}
          <a href={BOOK_A_CALL_URL} target="_blank" rel="noreferrer" className="text-electric-blue hover:underline">
            We'll run a custom Benchmark on your traffic
          </a>
          .
        </p>
      )}
      <p className="text-center text-xs">
        <Link to="/login" className="text-fg-muted hover:text-electric-blue">
          Already have an account? Sign in
        </Link>
      </p>
    </AuthCard>
  );
}
